import { useTranslation } from "react-i18next"
import clsx from "clsx"
import { useState, useEffect, ReactNode } from "react"
import { Helmet } from "react-helmet"

import ChevronLeft from "assets/Chevron-left.svg"
import ChevronRight from "assets/Chevron-right.svg"

const Carousel = ({
    slides,
    interval
  } : {
    slides: {
      title: ReactNode
      description: ReactNode
    }[],
    interval: number
  }
) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const nextSlide = () => {
    setCurrentIndex(
      (prevIndex) => prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    )
  }

  const prevSlide = () => {
    setCurrentIndex(
      (prevIndex) => prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    )
  }

  useEffect(
    () => {
      const slideInterval = setInterval(nextSlide, interval);

      return () => clearInterval(slideInterval);
    },
      [ currentIndex, interval ]
  );

  return (
    <>
      <div className="flex flex-row">
        <div className="shring place-content-center">
          <button className="focus:outline-none"
            onClick={prevSlide}
          >
            <img className="min-w-10 min-h-10 max-w-10 max-h-10" src={ChevronLeft} alt="Wstecz" />
          </button>
        </div>

        <div className="grow">
          <div className="w-full overflow-hidden grid grid-cols-1">
            {slides.map((slide, index) => (
              <div key={index}
                className={
                  clsx(
                    "w-full col-start-1 row-start-1 transition-transform duration-700 ease-in-out", index === currentIndex ? 'translate-x-0' : 'translate-x-full'
                  )
                }
                style={{
                    transform: `translateX(${(index - currentIndex) * 100}%)`
                  }
                }
              >
                <Slide title={slide.title} description={slide.description} />
              </div>
            ))}
          </div>
        </div>

        <div className="shring place-content-center">
          <button className="focus:outline-none"
            onClick={nextSlide}
          >
            <img className="min-w-10 min-h-10 max-w-10 max-h-10" src={ChevronRight} alt="Naprzód" />
          </button>
        </div>
      </div>

      <ul className="mt-12 hidden columns-2 columns-rule-2 columns-rule-solid columns-rule-aluprofil-light column-gap-12 lg:block">
        {slides.map(
            (slide, index) => (
              <li key={index}
                className={
                  clsx(
                    index === currentIndex ? "text-aluprofil-red" : "text-black/80", "cursor-pointer hover:text-aluprofil-red"
                  )
                }
                onClick={
                  () => setCurrentIndex(index)
                }
              >
                {slide.title}
              </li>
            )
          )
        }
      </ul>
    </>
  )
}

const Slide = ({
    title,
    description
  } : {
    title: ReactNode
    description: ReactNode
  }
) => {
  return (
    <div className="w-full flex flex-col items-center">
      <div className="flex flex-col gap-4 md:w-1/2">
        <h3 className="font-head font-bold text-2xl text-aluprofil-gray">
          {title}
        </h3>

        <div className="text-base text-aluprofil-gray">
          {description}
        </div>
      </div>
    </div>
  )
}

export const AdventagesComponent = () => {
  const { t } = useTranslation("Adventages")

  const slides: {
    title: ReactNode
    description: ReactNode
  }[] = [
    { title: t("Ponad 20-letnie doświadczenie"), description: t("Ponad 20-letnie doświadczenie.Description") },
    { title: t("Terminowy dostawca w kraju i na świecie"), description: t("Terminowy dostawca w kraju i na świecie.Description") },
    { title: t("Wysoka jakość produktów"), description: t("Wysoka jakość produktów.Description") },
    { title: t("Wsparcie regionalnych przedstawicieli handlowych"), description: t("Wsparcie regionalnych przedstawicieli handlowych.Description") },
    { title: t("Dożywotnia gwarancja na systemy jezdne"), description: t("Dożywotnia gwarancja na systemy jezdne.Description") },
    { title: t("Szkolenia teoretyczne i praktyczne"), description: t("Szkolenia teoretyczne i praktyczne.Description") },
    { title: t("Materiały marketingowe"), description: t("Materiały marketingowe.Description") },
    { title: t("Makiety i ekspozycje"), description: t("Makiety i ekspozycje.Description") },
    { title: t("Program Olimp"), description: t("Program Olimp.Description") },
    { title: t("Wysokie stany magazynowe zapewniające płynność dostaw"), description: t("Wysokie stany magazynowe zapewniające płynność dostaw.Description") },
    { title: t("Pomoce techniczne i montażowe"), description: t("Pomoce techniczne i montażowe.Description") },
    { title: t("Szeroki asortyment regularnie uzupełniany o akcesoria meblowe"), description: t("Szeroki asortyment regularnie uzupełniany o akcesoria meblowe.Description") },
    { title: t("Ekspert w technologii drzwi przesuwnych"), description: t("Ekspert w technologii drzwi przesuwnych.Description") },
    { title: t("Szeroka sieć dystrybucji zarówno w kraju jak i zagranicą"), description: t("Szeroka sieć dystrybucji zarówno w kraju jak i zagranicą.Description") },
    { title: t("Rozpoznawalna marka"), description: t("Rozpoznawalna marka.Description") },
    { title: t("Prawa autorskie i patentowe"), description: t("Prawa autorskie i patentowe.Description") },
    { title: t("Partnerstwo"), description: t("Partnerstwo.Description") },
    { title: t("Wysokie rabaty"), description: t("Wysokie rabaty.Description") },
    { title: t("Sprawna logistyka dostaw"), description: t("Sprawna logistyka dostaw.Description") },
    { title: t("Dział obsługi klienta"), description: t("Dział obsługi klienta.Description") },
    { title: t("Akcje promocyjne"), description: t("Akcje promocyjne.Description") },
    { title: t("Bogata gama kolorystyczna z dodatkową możliwością lakierowania"), description: t("Bogata gama kolorystyczna z dodatkową możliwością lakierowania.Description") },
    { title: t("Dbałość o szczegóły"), description: t("Dbałość o szczegóły.Description") },
    { title: t("Produkty dla każdego"), description: t("Produkty dla każdego.Description") },
  ]

  const titles = slides.map(slide => slide.title).join(' | ')

  return (
    <>
      <Helmet>
        <title>
          {t("Współpraca z ALUPROFIL")}
        </title>

        <meta
          property="og:type"
          content="website"
        />
        
        <meta
          property="og:title"
          content={t("Współpraca z ALUPROFIL")}
        />

        <meta
          property="og:description"
          content={titles}
        />

        <meta
          name="description"
          content={titles}
        />

        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "${t("Współpraca z ALUPROFIL")}",
            "description": "${titles}"
          }`}
        </script>
      </Helmet>

      <div className="w-full bg-white">
        <div className="mx-auto container">
          <div className="px-4 py-16">
            <hr className="mb-16 border-t-2 border-aluprofil-gray/20" />

            <h2 className="mb-16 w-full font-head font-normal text-5xl text-aluprofil-gray">
              {t("Atuty współpracy")}
            </h2>

            <Carousel interval={30 * 1000}
              slides={slides}
            />
          </div>
        </div>
      </div>
    </>
  )
}
